import axios from "axios";
import { API_BASE_URL } from "configs/AppConfig";
import { signOutSuccess } from "store/slices/authSlice";
import store from "../store";
import { AUTH_TOKEN, REFRESH_TOKEN } from "constants/AuthConstant";
import { notification } from "antd";
import i18n from "../lang";

const unauthorizedCode = [400, 401, 403];

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

const TOKEN_PAYLOAD_KEY = "authorization";
const REFRESH_TOKEN_ENDPOINT = "/jwt/refresh/";

// Helper function to get translated text
const t = (key) => {
  return i18n.t(key);
};

// Function to refresh access token
const refreshAccessToken = async () => {
  try {
    const refresh = localStorage.getItem(REFRESH_TOKEN);
    if (!refresh) {
      throw new Error(t("auth.no_refresh_token"));
    }

    const response = await axios.post(
      `${API_BASE_URL}${REFRESH_TOKEN_ENDPOINT}`,
      {
        refresh,
      }
    );

    const newAccessToken = response.data.accessToken;
    localStorage.setItem(AUTH_TOKEN, newAccessToken);
    return newAccessToken;
  } catch (error) {
    throw error;
  }
};

// Function to format error messages
const getErrorMessage = (error) => {
  if (error.response) {
    // Check for bilingual error response
    if (error.response.data?.error && error.response.data?.error_ar) {
      const currentLang = i18n.language || "en";
      return currentLang === "ar"
        ? error.response.data.error_ar
        : error.response.data.error;
    }

    if (typeof error.response.data?.message === "string") {
      return error.response.data.message;
    }
    if (typeof error.response.data === "string") {
      return error.response.data;
    }
    if (typeof error.response.data === "object") {
      return Object.entries(error.response.data)
        .map(
          ([key, value]) =>
            `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
        )
        .join(" | ");
    }
  }
  return error.message || t("auth.unknown_error");
};

// API Request interceptor
service.interceptors.request.use(
  (config) => {
    const jwtToken = localStorage.getItem(AUTH_TOKEN) || null;

    if (jwtToken) {
      config.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${jwtToken}`;
    }

    return config;
  },
  (error) => {
    notification.error({
      message: t("auth.error"),
      description: t("auth.request_failed"),
    });
    return Promise.reject(error);
  }
);

// API response interceptor
service.interceptors.response.use(
  (response) => {
    // Just return data, don't show success notification here
    // Let the component handle success notifications
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await refreshAccessToken();
        originalRequest.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${newAccessToken}`;

        return service(originalRequest);
      } catch (err) {
        notification.error({
          message: t("auth.authentication_failed"),
          description: t("auth.please_login_again"),
        });
        localStorage.removeItem(AUTH_TOKEN);
        localStorage.removeItem(REFRESH_TOKEN);
        store.dispatch(signOutSuccess());
        return Promise.reject(err);
      }
    }

    // Only show error notification for non-401 errors
    // Don't show notification here - let components handle their own error messages
    // This prevents double notifications

    return Promise.reject(error);
  }
);

export default service;
